import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  BookingConflict,
  PricingGroup,
  Zone,
  ZoneServiceDay,
} from '../../../services/externalDataService.service';
import { DistrictDto } from '../../../services/district.service';
import { BookingConflictPanelComponent } from '../../shared/booking-conflict-panel/booking-conflict-panel.component';

/** Zone fields edited in the form; the parent supplies the polygon from the map. */
export interface ZoneEditorValue {
  name: string;
  maxAppointmentsPerDay: number | null;
  maxAppointmentsPerSlot: number | null;
  pricingGroupId: string | null;
  districtId: string | null;
  /** null = every day, every slot. */
  serviceSchedule: ZoneServiceDay[] | null;
}

interface DayRow {
  day: number;
  label: string;
  enabled: boolean;
  allSlots: boolean;
  slots: Record<string, boolean>;
}

/** Monday-first display order; values are System.DayOfWeek (0 = Sunday). */
const DAY_ORDER: { day: number; label: string }[] = [
  { day: 1, label: 'Monday' },
  { day: 2, label: 'Tuesday' },
  { day: 3, label: 'Wednesday' },
  { day: 4, label: 'Thursday' },
  { day: 5, label: 'Friday' },
  { day: 6, label: 'Saturday' },
  { day: 0, label: 'Sunday' },
];

@Component({
  selector: 'app-zone-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, BookingConflictPanelComponent],
  templateUrl: './zone-editor.component.html',
})
export class ZoneEditorComponent implements OnChanges {
  /** Zone being edited; null when creating a new zone. */
  @Input() zone: Zone | null = null;
  @Input() pricingGroups: PricingGroup[] = [];
  @Input() districts: DistrictDto[] = [];
  @Input() timeSlots: string[] = [];
  /** Number of points in the polygon currently on the map (0 = not drawn yet). */
  @Input() pointCount = 0;
  @Input() saving = false;
  @Input() conflict: BookingConflict | null = null;

  @Output() save = new EventEmitter<ZoneEditorValue>();
  @Output() cancel = new EventEmitter<void>();
  @Output() conflictDismissed = new EventEmitter<void>();
  @Output() undoPoint = new EventEmitter<void>();
  @Output() clearPoints = new EventEmitter<void>();

  name = '';
  maxAppointmentsPerDay: number | null = null;
  maxAppointmentsPerSlot: number | null = null;
  pricingGroupId: string | null = null;
  districtId: string | null = null;
  everyDay = true;
  days: DayRow[] = [];
  errors: Record<string, string> = {};

  private seededFor: string | null = null;

  ngOnChanges(): void {
    // Re-seed only when the zone being edited or the slot list changes, not on saving/conflict flips.
    if (this.seededFor === this.seedKey()) {
      return;
    }
    this.seededFor = this.seedKey();
    this.seed();
  }

  get isNew(): boolean {
    return !this.zone;
  }

  toggleAllSlots(row: DayRow): void {
    if (row.allSlots) {
      this.timeSlots.forEach((slot) => (row.slots[slot] = true));
    }
  }

  submit(): void {
    this.errors = {};
    if (!this.name.trim()) {
      this.errors['name'] = 'Zone name is required.';
    }
    if (this.pointCount < 3) {
      this.errors['shape'] = 'Draw the zone on the map (at least 3 points).';
    }
    if (this.maxAppointmentsPerDay != null && this.maxAppointmentsPerDay < 1) {
      this.errors['maxAppointmentsPerDay'] = 'Must be at least 1, or empty for no limit.';
    }
    if (this.maxAppointmentsPerSlot != null && this.maxAppointmentsPerSlot < 1) {
      this.errors['maxAppointmentsPerSlot'] = 'Must be at least 1, or empty for no limit.';
    }

    const schedule = this.buildSchedule();
    if (!this.everyDay && schedule && schedule.length === 0) {
      this.errors['schedule'] = 'Select at least one service day, or choose "Every day".';
    }
    if (schedule?.some((d) => d.timeSlots && d.timeSlots.length === 0)) {
      this.errors['schedule'] = 'Each service day needs at least one time slot.';
    }
    if (Object.keys(this.errors).length > 0) {
      return;
    }

    this.save.emit({
      name: this.name.trim(),
      maxAppointmentsPerDay: this.toNullableNumber(this.maxAppointmentsPerDay),
      maxAppointmentsPerSlot: this.toNullableNumber(this.maxAppointmentsPerSlot),
      pricingGroupId: this.toNullableId(this.pricingGroupId),
      districtId: this.toNullableId(this.districtId),
      serviceSchedule: schedule,
    });
  }

  private seedKey(): string {
    return `${this.zone?.id ?? 'new'}|${this.timeSlots.join(',')}`;
  }

  private seed(): void {
    const zone = this.zone;
    this.errors = {};
    this.name = zone?.name ?? '';
    this.maxAppointmentsPerDay = zone?.maxAppointmentsPerDay ?? null;
    this.maxAppointmentsPerSlot = zone?.maxAppointmentsPerSlot ?? null;
    this.pricingGroupId = zone?.pricingGroupId ?? null;
    this.districtId = zone?.districtId ?? null;

    const schedule = zone?.serviceSchedule ?? [];
    this.everyDay = schedule.length === 0;
    this.days = DAY_ORDER.map(({ day, label }) => {
      const entry = schedule.find((d) => d.day === day);
      const ownSlots = entry?.timeSlots ?? [];
      const allSlots = ownSlots.length === 0;
      const slots: Record<string, boolean> = {};
      this.timeSlots.forEach((slot) => (slots[slot] = allSlots || ownSlots.includes(slot)));
      return { day, label, enabled: this.everyDay || !!entry, allSlots, slots };
    });
  }

  private buildSchedule(): ZoneServiceDay[] | null {
    if (this.everyDay) {
      return null;
    }
    return this.days
      .filter((row) => row.enabled)
      .map((row) => ({
        day: row.day,
        timeSlots: row.allSlots
          ? null
          : this.timeSlots.filter((slot) => row.slots[slot]),
      }));
  }

  private toNullableNumber(value: number | null | string): number | null {
    if (value === null || value === '' || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toNullableId(value: string | null): string | null {
    return !value || value === 'null' ? null : value;
  }
}
